import { isValidEmail, isValidPhone } from '@/utils'
import { Base } from '@itsrever/design-system'
import { useState, Fragment } from 'react'
import { useTranslation } from 'react-i18next'
import {
  ContactType,
  MAX_CONTACT_NAME_LENGTH,
  MAX_CONTACT_PHONE_LENGTH,
  isDeliverableEmail,
  missingOnboardingContactTypes
} from '@/domain/ecommerce-contact'
import {
  DashboardUsers as DashboardUsersInfo,
  OnboardingAction,
  OnboardingUser,
  onboardingContacts
} from '@/domain/onboarding'
import { ContactTypesSelect } from './contact-types-select'
import { contactTypeLabelKey } from './contact-types'

const { Button, FieldRoot, FieldLabel, FieldError, Input, Switch } = Base

const MAX_USERS = 5

interface DashboardUsersProps {
  dashboardUsers: DashboardUsersInfo
  dispatch: React.Dispatch<OnboardingAction>
}

export const DashboardUsers: React.FC<DashboardUsersProps> = ({
  dashboardUsers,
  dispatch
}) => {
  const { t } = useTranslation()
  const { users } = dashboardUsers
  const [name, setName] = useState('')
  const [phone, setPhone] = useState('')
  const [email, setEmail] = useState('')
  const [contactTypesPicked, setContactTypesPicked] = useState<ContactType[]>(
    []
  )

  const updateUsers = (next: OnboardingUser[]) =>
    dispatch({ type: 'UPDATE_DASHBOARD_USERS', payload: { users: next } })

  const trimmedEmail = email.trim()
  const invalidEmail = trimmedEmail.length > 0 && !isValidEmail(trimmedEmail)
  // Every user is a point of contact, and settings-api refuses emails that parse but bounce.
  const undeliverableEmail =
    trimmedEmail.length > 0 &&
    !invalidEmail &&
    !isDeliverableEmail(trimmedEmail)
  const userAlreadyExists = users.some(
    (user) => user.email.toLowerCase() === trimmedEmail.toLowerCase()
  )
  const invalidPhone = phone.trim().length > 0 && !isValidPhone(phone.trim())
  const emailError = invalidEmail
    ? t('onboarding.error_email')
    : undeliverableEmail
      ? t('dashboard_users.point_of_contact_invalid_email')
      : userAlreadyExists
        ? t('onboarding.user_already_exists')
        : ''
  const maxUsers = users.length >= MAX_USERS
  const missingTypes = missingOnboardingContactTypes(onboardingContacts(users))
  // Users cannot be edited or removed, so the last free slot must cover the required topics still missing.
  const lastSlotMissesTopics =
    users.length === MAX_USERS - 1 &&
    !missingTypes.every((type) => contactTypesPicked.includes(type))
  const canAdd =
    trimmedEmail.length > 0 &&
    !emailError &&
    !invalidPhone &&
    contactTypesPicked.length > 0 &&
    !maxUsers &&
    !lastSlotMissesTopics

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (!canAdd) return

    updateUsers([
      ...users,
      {
        email: trimmedEmail,
        name: name.trim(),
        phone: phone.trim(),
        role: 'admin',
        contactTypes: contactTypesPicked
      }
    ])
    setName('')
    setPhone('')
    setEmail('')
    setContactTypesPicked([])
  }

  const handleChangeRole = (index: number) =>
    updateUsers(
      users.map((user, i) =>
        i === index
          ? { ...user, role: user.role === 'admin' ? 'user' : 'admin' }
          : user
      )
    )

  return (
    <div className="grid grid-cols-[2fr_3fr] gap-6">
      <div className="flex flex-col gap-3">
        <div>
          <h6>{t('onboarding.create_new_user')}</h6>
          <p className="text-xs text-neutral-600">
            {t('onboarding.user_description')}
          </p>
        </div>
        <form className="flex flex-col gap-3" onSubmit={handleSubmit}>
          <div className="grid grid-cols-2 gap-3">
            <FieldRoot>
              <FieldLabel>{t('dashboard_users.contact_name')}</FieldLabel>
              <Input
                autoComplete="off"
                maxLength={MAX_CONTACT_NAME_LENGTH}
                disabled={maxUsers}
                value={name}
                onChange={(e) => setName(e.currentTarget.value)}
              />
            </FieldRoot>
            <FieldRoot invalid={invalidPhone}>
              <FieldLabel>{t('dashboard_users.contact_phone')}</FieldLabel>
              <Input
                type="tel"
                autoComplete="off"
                maxLength={MAX_CONTACT_PHONE_LENGTH}
                disabled={maxUsers}
                value={phone}
                onChange={(e) => setPhone(e.currentTarget.value)}
              />
              {invalidPhone ? (
                <FieldError match>
                  {t('dashboard_users.invalid_phone')}
                </FieldError>
              ) : null}
            </FieldRoot>
          </div>
          <FieldRoot invalid={!!emailError}>
            <FieldLabel>{t('onboarding.email')} *</FieldLabel>
            <Input
              type="email"
              autoComplete="off"
              placeholder="example@itsrever.com"
              disabled={maxUsers}
              value={email}
              onChange={(e) => setEmail(e.currentTarget.value)}
            />
            {emailError ? <FieldError match>{emailError}</FieldError> : null}
          </FieldRoot>
          <FieldRoot>
            <FieldLabel>{t('dashboard_users.point_of_contact')} *</FieldLabel>
            <ContactTypesSelect
              value={contactTypesPicked}
              onChange={setContactTypesPicked}
              disabled={maxUsers}
              placeholder={t('onboarding.select_topics')}
            />
          </FieldRoot>
          <Button
            type="submit"
            variant="primary"
            className="self-start"
            disabled={!canAdd}
          >
            {t('onboarding.add_user')}
          </Button>
        </form>
      </div>

      <div className="flex flex-col gap-3">
        <div>
          <p className="text-sm text-grey-1">
            <li>{t('onboarding.regular_user_description')}</li>
            <li>{t('onboarding.admin_user_description')}</li>
          </p>
          <p className="mt-2 text-sm text-grey-1">
            {users.length} / {MAX_USERS}
          </p>
          {maxUsers ? (
            <p className="text-xs text-error-main">
              {t('onboarding.max_users_created')}
            </p>
          ) : null}
        </div>
        <div className="rounded-lg bg-blue-50 p-4 text-blue-800">
          <div className="grid grid-cols-[4fr_5fr_auto_auto] items-center gap-3">
            <p className="text-left text-blue-500">{t('onboarding.user')}</p>
            <p className="text-left text-blue-500">
              {t('dashboard_users.point_of_contact')}
            </p>
            <p className="text-left text-blue-500">{t('onboarding.role')}</p>
            <p className="text-left text-blue-500">Admin</p>
            {users.map((user, i) => (
              <Fragment key={user.email}>
                <div className="min-w-0">
                  <p className="truncate text-sm">{user.email}</p>
                  {user.name || user.phone ? (
                    <p className="truncate text-xs text-neutral-600">
                      {[user.name, user.phone].filter(Boolean).join(' · ')}
                    </p>
                  ) : null}
                </div>
                <p className="text-sm">
                  {user.contactTypes
                    .map((type) => t(contactTypeLabelKey(type)))
                    .join(', ')}
                </p>
                <p className="truncate">
                  {user.role.charAt(0).toUpperCase() + user.role.slice(1)}
                </p>
                <Switch
                  checked={user.role === 'admin'}
                  onCheckedChange={() => handleChangeRole(i)}
                  aria-label={`Admin ${user.email}`}
                />
              </Fragment>
            ))}
          </div>
        </div>
        {missingTypes.length > 0 ? (
          <p className="text-xs text-neutral-600">
            {t('onboarding.required_topics', {
              topics: missingTypes
                .map((type) => t(contactTypeLabelKey(type)))
                .join(', ')
            })}
          </p>
        ) : null}
      </div>
    </div>
  )
}
